import React, { useMemo, useRef, useState, useCallback } from 'react';
import { View, StyleSheet, Image, ScrollView, useWindowDimensions, TouchableOpacity, NativeSyntheticEvent, NativeScrollEvent, StatusBar } from 'react-native';
import { Text } from '@/components/common/text';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { ONBOARDING_TUTORIAL_STRINGS as T } from '@/components/residents/tutorial/string';
import { GradientBackground } from '@/components/residents/redesign';

type Slide = {
  title: string;
  descr: string;
  image: any;
};

export const TutorialScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation<any>();
  const { width, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const scrollRef = useRef<ScrollView>(null);
  const [index, setIndex] = useState(0);
  const topHeight = useMemo(() => Math.round(height * 0.7), [height]);
  const headerTop = useMemo(() => insets.top, [insets.top]);

  const slides = useMemo<Slide[]>(() => [
    { title: T.title1, descr: T.descr1, image: require('@/assets/residents/Onboarding/ob_image_1.imageset/ob_image_1.png') },
    { title: T.title2, descr: T.descr2, image: require('@/assets/residents/Onboarding/ob_image_2.imageset/ob_image_2.png') },
    { title: T.title3, descr: T.descr3, image: require('@/assets/residents/Onboarding/ob_image_3.imageset/ob_image_3.png') },
    { title: T.title4, descr: T.descr4, image: require('@/assets/residents/Onboarding/ob_image_4.imageset/ob_image_4.png') },
    { title: T.title5, descr: T.descr5, image: require('@/assets/residents/Onboarding/ob_image_5.imageset/ob_image_5.png') },
    { title: T.title6, descr: T.descr6, image: require('@/assets/residents/Onboarding/ob_image_6.imageset/ob_image_6.png') },
  ], []);

  const onScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const x = e.nativeEvent.contentOffset.x;
    const newIndex = Math.round(x / width);
    if (newIndex !== index) setIndex(newIndex);
  }, [index, width]);

  const onSkip = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  return (
    <GradientBackground>
      <StatusBar barStyle="light-content" />
      <SafeAreaView style={styles.container} edges={['bottom']}>
        <View style={[styles.header, { top: headerTop, paddingTop: 0 }]}>
          <TouchableOpacity accessibilityRole="button" onPress={onSkip} style={[styles.skipButton, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]}>
            <Text style={[styles.skipText, { color: colors.text.secondary }]}>{T.btn.skip}</Text>
          </TouchableOpacity>
        </View>

        <ScrollView
          ref={scrollRef}
          horizontal
          pagingEnabled
          onScroll={onScroll}
          scrollEventThrottle={16}
          showsHorizontalScrollIndicator={false}
          contentInsetAdjustmentBehavior="never"
          contentContainerStyle={{ alignItems: 'stretch' }}
        >
          {slides.map((s, i) => (
            <View key={i} style={[styles.slide, { width }]}>
              <View style={[styles.imageWrap, { height: topHeight }]}>
                <Image source={s.image} resizeMode="cover" style={styles.image} />
              </View>
              <ScrollView style={styles.textScroll} contentContainerStyle={styles.textContent} showsVerticalScrollIndicator={false}>
                <Text style={[styles.title, { color: colors.text.primary }]}>{s.title}</Text>
                <Text scale="content" style={[styles.descr, { color: colors.text.muted }]}>{s.descr}</Text>
              </ScrollView>
            </View>
          ))}
        </ScrollView>

        <View style={styles.footer}>
          <View style={styles.dotsRow}>
            {slides.map((_, i) => (
              <View key={`dot-${i}`} style={[styles.dot, { backgroundColor: colors.text.dimmer }, i === index && styles.dotActive]} />
            ))}
          </View>
        </View>
      </SafeAreaView>
    </GradientBackground>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 0,
    alignItems: 'flex-end',
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    zIndex: 10,
  },
  skipButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  skipText: {
    fontSize: 14,
    fontWeight: '600',
  },
  slide: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  imageWrap: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  image: {
    width: '100%',
    height: '100%',
  },
  textScroll: {
    flex: 1,
    alignSelf: 'stretch',
  },
  textContent: {
    alignItems: 'center',
    paddingBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: '800',
    textAlign: 'center',
    marginBottom: 8,
    paddingHorizontal: 20,
  },
  descr: {
    fontSize: 14,
    textAlign: 'center',
    paddingHorizontal: 16,
  },
  footer: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  dotsRow: {
    flexDirection: 'row',
    gap: 8,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  dotActive: {
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
  },
});

export default TutorialScreen;
